import {
  ProjectSpecification,
  ProductSubstitution,
  SpecificationQuery,
  SubstitutionQuery,
  CreateSpecificationData,
  UpdateSpecificationData,
  CreateSubstitutionData,
  ReviewSubstitutionData,
  CSIDivision,
  FileReference,
  SubstitutionFileType,
} from '../types';
import { apiService as api } from './httpClient';

// Response interfaces
interface SpecificationsListResponse {
  specifications: ProjectSpecification[];
  pagination?: {
    total: number;
    page: number;
    totalPages: number;
  };
  total?: number;
  page?: number;
  totalPages?: number;
}

interface SubstitutionsListResponse {
  substitutions: ProductSubstitution[];
  pagination?: {
    total: number;
    page: number;
    totalPages: number;
  };
  total?: number;
  page?: number;
  totalPages?: number;
}

interface SearchResponse {
  specifications: ProjectSpecification[];
  total: number;
}

interface MutationResponse {
  message?: string;
  success?: boolean;
}

class SpecificationService {
  // Helper method to get the correct API endpoint
  private getSpecsEndpoint(projectId: string): string {
    return `/projects/${projectId}/specifications`;
  }

  // =====================
  // SPECIFICATION OPERATIONS
  // =====================

  // Get specifications with filtering and pagination
  async getSpecifications(projectId: string, query: SpecificationQuery = {}) {
    try {
      const params = new URLSearchParams();

      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const endpoint = `${this.getSpecsEndpoint(projectId)}?${params.toString()}`;
      const response = await api.get<SpecificationsListResponse>(endpoint);
      const data = response.data as SpecificationsListResponse;

      return {
        success: true,
        specifications: data.specifications || [],
        total: data.pagination?.total || data.total || 0,
        page: data.pagination?.page || data.page || 1,
        totalPages: data.pagination?.totalPages || data.totalPages || 1,
      };
    } catch (error) {
      console.error('Failed to fetch specifications:', error);
      return {
        success: false,
        message: 'Failed to load specifications',
        specifications: [],
        total: 0,
        page: 1,
        totalPages: 1,
      };
    }
  }

  // Get single specification by ID
  async getSpecification(projectId: string, specId: string) {
    try {
      const endpoint = `${this.getSpecsEndpoint(projectId)}/${specId}`;
      const response = await api.get<ProjectSpecification>(endpoint);
      return {
        success: true,
        specification: response.data as ProjectSpecification,
      };
    } catch (error) {
      console.error('Failed to fetch specification:', error);
      return {
        success: false,
        message: 'Failed to load specification',
      };
    }
  }

  // Create a specification (after S3 upload)
  async createSpecification(
    projectId: string,
    fileReference: FileReference,
    specData: CreateSpecificationData
  ) {
    try {
      const endpoint = this.getSpecsEndpoint(projectId);
      const response = await api.post<ProjectSpecification>(endpoint, {
        file_reference: fileReference,
        ...specData,
      });
      const data = response.data as ProjectSpecification;

      return {
        success: true,
        specification: data,
        message: 'Specification created successfully',
      };
    } catch (error) {
      console.error('Failed to create specification:', error);
      return {
        success: false,
        message: 'Failed to create specification',
      };
    }
  }

  // Update specification metadata
  async updateSpecification(projectId: string, specId: string, specData: UpdateSpecificationData) {
    try {
      const endpoint = `${this.getSpecsEndpoint(projectId)}/${specId}`;
      const response = await api.put<ProjectSpecification>(endpoint, specData);
      const data = response.data as ProjectSpecification;

      return {
        success: true,
        specification: data,
        message: 'Specification updated successfully',
      };
    } catch (error) {
      console.error('Failed to update specification:', error);
      return {
        success: false,
        message: 'Failed to update specification',
      };
    }
  }

  // Delete a specification
  async deleteSpecification(projectId: string, specId: string) {
    try {
      const endpoint = `${this.getSpecsEndpoint(projectId)}/${specId}`;
      const response = await api.delete<MutationResponse>(endpoint);
      const data = response.data as MutationResponse;

      return {
        success: true,
        message: data.message || 'Specification deleted successfully',
      };
    } catch (error) {
      console.error('Failed to delete specification:', error);
      return {
        success: false,
        message: 'Failed to delete specification',
      };
    }
  }

  // Add a new version to a specification
  async addSpecificationVersion(
    projectId: string,
    specId: string,
    fileReference: FileReference,
    description?: string
  ) {
    try {
      const endpoint = `${this.getSpecsEndpoint(projectId)}/${specId}/versions`;
      const response = await api.post<ProjectSpecification>(endpoint, {
        file_reference: fileReference,
        description,
      });
      const data = response.data as ProjectSpecification;

      return {
        success: true,
        specification: data,
        message: 'New version added successfully',
      };
    } catch (error) {
      console.error('Failed to add specification version:', error);
      return {
        success: false,
        message: 'Failed to add new version',
      };
    }
  }

  // Search specifications
  async searchSpecifications(projectId: string, query: string, division?: string) {
    try {
      const params = new URLSearchParams({ q: query });
      if (division) {
        params.append('division', division);
      }

      const endpoint = `${this.getSpecsEndpoint(projectId)}/search?${params.toString()}`;
      const response = await api.get<SearchResponse>(endpoint);
      const data = response.data as SearchResponse;

      return {
        success: true,
        specifications: data.specifications || [],
        total: data.total || 0,
      };
    } catch (error) {
      console.error('Failed to search specifications:', error);
      return {
        success: false,
        message: 'Failed to search specifications',
        specifications: [],
        total: 0,
      };
    }
  }

  // Get CSI divisions (static reference data - no API call needed)
  getCSIDivisions() {
    return {
      success: true,
      divisions: this.getDefaultCSIDivisions(),
    };
  }

  // =====================
  // SUBSTITUTION OPERATIONS
  // =====================

  // Get substitutions with filtering and pagination
  async getSubstitutions(projectId: string, query: SubstitutionQuery = {}) {
    try {
      const params = new URLSearchParams();

      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const endpoint = `${this.getSpecsEndpoint(projectId)}/substitutions/list?${params.toString()}`;
      const response = await api.get<SubstitutionsListResponse>(endpoint);
      const data = response.data as SubstitutionsListResponse;

      return {
        success: true,
        substitutions: data.substitutions || [],
        total: data.pagination?.total || data.total || 0,
        page: data.pagination?.page || data.page || 1,
        totalPages: data.pagination?.totalPages || data.totalPages || 1,
      };
    } catch (error) {
      console.error('Failed to fetch substitutions:', error);
      return {
        success: false,
        message: 'Failed to load substitutions',
        substitutions: [],
        total: 0,
        page: 1,
        totalPages: 1,
      };
    }
  }

  // Get single substitution by ID
  async getSubstitution(projectId: string, substitutionId: string) {
    try {
      const endpoint = `${this.getSpecsEndpoint(projectId)}/substitutions/${substitutionId}`;
      const response = await api.get<ProductSubstitution>(endpoint);
      return {
        success: true,
        substitution: response.data as ProductSubstitution,
      };
    } catch (error) {
      console.error('Failed to fetch substitution:', error);
      return {
        success: false,
        message: 'Failed to load substitution',
      };
    }
  }

  // Create a substitution request
  async createSubstitution(projectId: string, subData: CreateSubstitutionData) {
    try {
      const endpoint = `${this.getSpecsEndpoint(projectId)}/substitutions`;
      const response = await api.post<ProductSubstitution>(endpoint, subData);
      const data = response.data as ProductSubstitution;

      return {
        success: true,
        substitution: data,
        message: 'Substitution request created successfully',
      };
    } catch (error) {
      console.error('Failed to create substitution:', error);
      return {
        success: false,
        message: 'Failed to create substitution request',
      };
    }
  }

  // Review a substitution
  async reviewSubstitution(
    projectId: string,
    substitutionId: string,
    reviewData: ReviewSubstitutionData
  ) {
    try {
      const endpoint = `${this.getSpecsEndpoint(projectId)}/substitutions/${substitutionId}/review`;
      const response = await api.put<ProductSubstitution>(endpoint, reviewData);
      const data = response.data as ProductSubstitution;

      return {
        success: true,
        substitution: data,
        message: 'Substitution reviewed successfully',
      };
    } catch (error) {
      console.error('Failed to review substitution:', error);
      return {
        success: false,
        message: 'Failed to review substitution',
      };
    }
  }

  // Add attachment to substitution
  async addSubstitutionAttachment(
    projectId: string,
    substitutionId: string,
    fileReference: FileReference,
    fileType: SubstitutionFileType = 'other'
  ) {
    try {
      const endpoint = `${this.getSpecsEndpoint(projectId)}/substitutions/${substitutionId}/attachments`;
      const response = await api.post<ProductSubstitution>(endpoint, {
        file_reference: fileReference,
        file_type: fileType,
      });
      const data = response.data as ProductSubstitution;

      return {
        success: true,
        substitution: data,
        message: 'Attachment added successfully',
      };
    } catch (error) {
      console.error('Failed to add attachment:', error);
      return {
        success: false,
        message: 'Failed to add attachment',
      };
    }
  }

  // Delete a substitution
  async deleteSubstitution(projectId: string, substitutionId: string) {
    try {
      const endpoint = `${this.getSpecsEndpoint(projectId)}/substitutions/${substitutionId}`;
      const response = await api.delete<MutationResponse>(endpoint);
      const data = response.data as MutationResponse;

      return {
        success: true,
        message: data.message || 'Substitution deleted successfully',
      };
    } catch (error) {
      console.error('Failed to delete substitution:', error);
      return {
        success: false,
        message: 'Failed to delete substitution',
      };
    }
  }

  // =====================
  // UTILITY METHODS
  // =====================

  // Get default CSI divisions
  getDefaultCSIDivisions(): CSIDivision[] {
    return [
      { code: '00', name: 'Procurement and Contracting Requirements' },
      { code: '01', name: 'General Requirements' },
      { code: '02', name: 'Existing Conditions' },
      { code: '03', name: 'Concrete' },
      { code: '04', name: 'Masonry' },
      { code: '05', name: 'Metals' },
      { code: '06', name: 'Wood, Plastics, and Composites' },
      { code: '07', name: 'Thermal and Moisture Protection' },
      { code: '08', name: 'Openings' },
      { code: '09', name: 'Finishes' },
      { code: '10', name: 'Specialties' },
      { code: '11', name: 'Equipment' },
      { code: '12', name: 'Furnishings' },
      { code: '13', name: 'Special Construction' },
      { code: '14', name: 'Conveying Equipment' },
      { code: '21', name: 'Fire Suppression' },
      { code: '22', name: 'Plumbing' },
      { code: '23', name: 'Heating, Ventilating, and Air Conditioning (HVAC)' },
      { code: '25', name: 'Integrated Automation' },
      { code: '26', name: 'Electrical' },
      { code: '27', name: 'Communications' },
      { code: '28', name: 'Electronic Safety and Security' },
      { code: '31', name: 'Earthwork' },
      { code: '32', name: 'Exterior Improvements' },
      { code: '33', name: 'Utilities' },
      { code: '34', name: 'Transportation' },
      { code: '35', name: 'Waterway and Marine Construction' },
      { code: '40', name: 'Process Interconnections' },
      { code: '41', name: 'Material Processing and Handling Equipment' },
      { code: '42', name: 'Process Heating, Cooling, and Drying Equipment' },
      { code: '43', name: 'Process Gas and Liquid Handling, Purification, and Storage Equipment' },
      { code: '44', name: 'Pollution and Waste Control Equipment' },
      { code: '45', name: 'Industry-Specific Manufacturing Equipment' },
      { code: '46', name: 'Water and Wastewater Equipment' },
      { code: '48', name: 'Electrical Power Generation' },
    ];
  }

  // Get specification statuses for dropdown
  getSpecificationStatuses() {
    return [
      { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-800' },
      { value: 'issued', label: 'Issued', color: 'bg-green-100 text-green-800' },
      { value: 'revised', label: 'Revised', color: 'bg-yellow-100 text-yellow-800' },
      { value: 'superseded', label: 'Superseded', color: 'bg-orange-100 text-orange-800' },
      { value: 'archived', label: 'Archived', color: 'bg-red-100 text-red-800' },
    ];
  }

  // Get substitution statuses for dropdown
  getSubstitutionStatuses() {
    return [
      { value: 'submitted', label: 'Submitted', color: 'bg-blue-100 text-blue-800' },
      { value: 'under_review', label: 'Under Review', color: 'bg-yellow-100 text-yellow-800' },
      { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-800' },
      { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
      {
        value: 'approved_as_noted',
        label: 'Approved as Noted',
        color: 'bg-cyan-100 text-cyan-800',
      },
    ];
  }

  // Get substitution file types for dropdown
  getSubstitutionFileTypes() {
    return [
      { value: 'cut_sheet', label: 'Cut Sheet' },
      { value: 'comparison', label: 'Comparison' },
      { value: 'certification', label: 'Certification' },
      { value: 'other', label: 'Other' },
    ];
  }

  // Get sort options for dropdown
  getSortOptions() {
    return [
      { value: 'section_number', label: 'Section Number' },
      { value: 'title', label: 'Title' },
      { value: 'division', label: 'Division' },
      { value: 'status', label: 'Status' },
      { value: 'created_at', label: 'Date Added' },
      { value: 'updated_at', label: 'Date Modified' },
    ];
  }

  // Get status color
  getStatusColor(status: string): string {
    const statuses = this.getSpecificationStatuses();
    const found = statuses.find(s => s.value === status);
    return found?.color || 'bg-gray-100 text-gray-800';
  }

  // Get substitution status color
  getSubstitutionStatusColor(status: string): string {
    const statuses = this.getSubstitutionStatuses();
    const found = statuses.find(s => s.value === status);
    return found?.color || 'bg-gray-100 text-gray-800';
  }

  // Format section number for display
  formatSectionNumber(sectionNumber: string): string {
    // Convert 033000 to 03 30 00 format if needed
    const cleaned = sectionNumber.replace(/\s/g, '');
    if (cleaned.length >= 6) {
      return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4)}`;
    }
    return sectionNumber;
  }

  // Get division name from code
  getDivisionName(code: string): string {
    const divisions = this.getDefaultCSIDivisions();
    const found = divisions.find(d => d.code === code);
    return found?.name || `Division ${code}`;
  }
}

export default new SpecificationService();
